import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Sparkles, RefreshCw, Loader2, AlertTriangle, Target, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

/**
 * Bloc résumé IA en haut de la fiche client.
 * 24h cache backend. Bouton régénérer pour forcer.
 */
const ClientAIBriefCard = ({ clientId }) => {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [error, setError] = useState(null);

  const fetchBrief = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/clients/${clientId}/brief`);
      const data = await res.json();
      if (data.success) {
        setBrief(data.brief);
        setGeneratedAt(data.generated_at);
      } else if (data.brief) {
        setBrief(data.brief);
      } else {
        setBrief(null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const res = await api.post(`/admin/clients/${clientId}/brief/regenerate`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setBrief(data.brief);
      setGeneratedAt(data.generated_at);
      toast.success('Résumé IA régénéré');
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setRegenerating(false);
    }
  };

  useEffect(() => { fetchBrief(); }, [fetchBrief]);

  if (loading) {
    return (
      <Card className="p-4 md:p-5 border-tp-primary/20 bg-tp-primary/5" data-testid="client-ai-brief-loading">
        <div className="flex items-center gap-2 text-tp-primary">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Chargement du résumé IA...</span>
        </div>
      </Card>
    );
  }

  // Empty state — propose generation
  if (!brief) {
    return (
      <Card className="p-4 md:p-5 border-tp-primary/30 bg-tp-primary/5" data-testid="client-ai-brief-empty">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-tp-primary/10 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-4 w-4 text-tp-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-tp-text">Résumé IA · pré-call</h3>
              <p className="text-xs text-tp-text-light mt-0.5">
                Génère un brief commercial avant ton prochain call avec ce client.
              </p>
              {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
            </div>
          </div>
          <Button
            size="sm"
            onClick={regenerate}
            disabled={regenerating}
            className="bg-tp-primary hover:bg-tp-primary-light text-white whitespace-nowrap"
            data-testid="client-ai-brief-generate-btn"
          >
            {regenerating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Générer le résumé
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 md:p-5 border-tp-primary/30 bg-gradient-to-br from-tp-primary/5 to-transparent" data-testid="client-ai-brief">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-tp-primary/15 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-4 w-4 text-tp-primary" />
          </div>
          <h3 className="text-sm font-semibold text-tp-text uppercase tracking-wide">Résumé IA · pré-call</h3>
        </div>
        <button
          onClick={regenerate}
          disabled={regenerating}
          className="text-xs text-tp-text-light hover:text-tp-primary inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="client-ai-brief-regen-btn"
          title="Régénérer le résumé"
        >
          {regenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          {regenerating ? 'Génération...' : 'Régénérer'}
        </button>
      </div>

      <div className="space-y-3">
        {brief.summary && (
          <p className="text-sm text-tp-text leading-snug" data-testid="client-ai-brief-summary">
            {brief.summary}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {brief.potential && (
            <div className="rounded-md bg-white/60 border border-tp-border/40 p-2.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-tp-text-light uppercase tracking-wide mb-1">
                <Target className="h-3 w-3" />
                Potentiel
              </div>
              <p className="text-xs text-tp-text leading-snug">{brief.potential}</p>
            </div>
          )}
          {brief.next_action && (
            <div className="rounded-md bg-white/60 border border-tp-border/40 p-2.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-tp-text-light uppercase tracking-wide mb-1">
                <ArrowRight className="h-3 w-3" />
                Prochaine action
              </div>
              <p className="text-xs text-tp-text leading-snug">{brief.next_action}</p>
            </div>
          )}
        </div>

        {Array.isArray(brief.warnings) && brief.warnings.length > 0 && (
          <div className="rounded-md bg-orange-50 border border-orange-200 p-2.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-orange-700 uppercase tracking-wide mb-1">
              <AlertTriangle className="h-3 w-3" />
              Points d'attention
            </div>
            <ul className="space-y-0.5">
              {brief.warnings.map((w, i) => (
                <li key={`warn-${i}`} className="text-xs text-orange-700 leading-snug">• {w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {generatedAt && (
        <p className="text-[10px] text-tp-text-light mt-3 italic">
          Généré le {new Date(generatedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
        </p>
      )}
    </Card>
  );
};

export default ClientAIBriefCard;
